
import LeadContent from "pages/shared/lead-content/LeadContent"
import Loading from "pages/shared/loading/Loading"
import { useContext, useEffect, useState } from "react"
import { useErrorBoundary } from "react-error-boundary"
import { useNavigate, useParams } from "react-router"
import { NotificationContext } from "shared/NotificationContext"
import backofficeApi from 'shared/topchef-api-backoffice';
import { Lead, LeadWithBill, LocHallBill, NotificationType } from "shared/topchef.types"

const BackofficeBillToValidate = () => {

  const navigate = useNavigate();
  
  const notificationContext = useContext(NotificationContext);
  const { triggerNotification } = notificationContext;

  const params = useParams();
  const leadId: string = params.leadId || '';

  const [isReady, setReady] = useState<boolean>(false);
  const [leadToValidate, setLeadToValidate] = useState<LeadWithBill | null>(null);
  const [locHallBill, setLocHallBill] = useState<LocHallBill>({
    billAmount : '',
    sourceFee : '',
    locHallFee : ''
  });

  const { showBoundary } = useErrorBoundary();

  useEffect(() => {

    async function loadLead() {
      try {
        const lead: LeadWithBill = await backofficeApi.loadLeadWithBillToValidate(leadId);
        setLeadToValidate(lead);
        setLocHallBill({
          billAmount : lead.billAmount,
          sourceFee : lead.sourceFee,
          locHallFee : lead.locHallFee
        });

        setReady(true);
      } catch (error: any) { 
        const newError = new Error(`Problème pour récupérer le brief à valider : '${error.message}'`);
        triggerNotification({type: NotificationType.error, message : newError.message}); 
        showBoundary(newError);
      } 
    }

    if (isReady) {
      return;
    }

    loadLead();

  }, [isReady, leadId, showBoundary, triggerNotification]);

  const handleInputEventChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const { name, value } = event.target;
    setLocHallBill((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    
  };

  const validateLeadBill = async (event: React.MouseEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await backofficeApi.validateBill(leadId, locHallBill);
      triggerNotification({type: NotificationType.information, message : 'Facture validée 🎉🎉🎉'});
      navigate('/backoffice/leads/bills-to-validate');
    } catch (error: any) {
      const newError = new Error(`Erreur lors de la validation de la factrure : ${error.message}`);
      triggerNotification({type: NotificationType.error, message : `${newError.message}`});
      showBoundary(newError);
    }
  };

  const downloadBill = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    try {
      await backofficeApi.downloadBill(leadId);
      triggerNotification({type: NotificationType.information, message : 'Téléchargement de la facture lancé 🚀'});
    } catch (error: any) {
      const newError = new Error(`Erreur lors du téléchargement de la facture : ${error.message}`);
      triggerNotification({type: NotificationType.error, message : `${newError.message}`});
      showBoundary(newError);
    }
  }

  if (!isReady) {
    return (
      <Loading />
    );
  }

  
  return (
    <div className="container p-5 overflow-auto backoffice-bill-to-validate">
      <h4 className="mb-3 text-center w-100">Valider la facture</h4>

      <div className="align-items-center justify-content-center col-md-4">
        <div id="event_details_form" className="col-md-12">
          
          <form id="invoice_details_form" className="d-flex row justify-content-center p-3 pb-4" onSubmit={validateLeadBill}>

            <div className="row text-center justify-content-center">
              <div className="mb-4 col-md-12">
                <label htmlFor="invoice_price" className="form-label">Montant de la facture</label>
                <input type="number" name="billAmount" id="invoice_price" className="form-control w-50 m-auto" min={1} placeholder="Montant de la facture HT en €" defaultValue={locHallBill!.billAmount} required onChange={handleInputEventChange} />
              </div>
            </div>
            <div className="row text-center justify-content-center">
              <div className="mb-4 col-md-12">
                <label htmlFor="source_fee" className="form-label">Commission pour {leadToValidate!.source}</label>
                <input type="number" name="sourceFee" id="source_fee" className="form-control w-50 m-auto" min={1} placeholder="Montant de la commission pour la place HT en €" defaultValue={locHallBill!.sourceFee} required onChange={handleInputEventChange} />
              </div>
            </div>
            <div className="row text-center justify-content-center">
              <div className="mb-4 col-md-12">
                <label htmlFor="lochall_fee" className="form-label">Commission pour Loc'Hall</label>
                <input type="number" name="locHallFee" id="lochall_fee" className="form-control w-50 m-auto" min={1} placeholder="Montant de la commission pour Loc'Hall HT en €" defaultValue={locHallBill!.locHallFee} required onChange={handleInputEventChange} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 text-center">
                <button className="btn btn-link" onClick={downloadBill}>Télécharger la facture</button>
              </div>     
            </div>
            <div className="text-center">
              {leadToValidate!.status === 'billed' &&
                <button type="submit" id="save_brief_details" className="btn btn-primary m-3">Valider la facture</button>
              }
              {leadToValidate!.status === 'verified' &&
                <div className="alert alert-success" role="alert">
                  Facture validée ! 🎉
                </div>
              }
            </div>
          </form>
        </div>
      </div>

      <div className="align-items-center justify-content-center col-md-6">
        <div className="col-md-12">
          <LeadContent lead={leadToValidate as Lead} />
        </div>
      </div>
    </div>
  );

}

export default BackofficeBillToValidate;
